import { v4 as uuidv4 } from 'uuid';
import { createBasicActionTypes, createErrorAction, createInProgressAction, createSuccessAction } from 'src/utils/createAction';
import { getPayloadFromResponse } from 'src/utils/api/core';
import { addErrorToast, addSuccessToast } from 'src/store/toast/actions';
import TOAST_MESSAGES from 'src/utils/toastMessages';
import { getTugStages, addTugStage, updateTugStage, deleteTugStage, updateTugStages } from 'src/utils/tugStagesApi';
import { entityByUuidSelector } from 'src/store/sync/selectors';
import { isOnlineSelector } from 'src/store/ui/selectors';
import { addEntity } from 'src/store/sync/actions';
import { SYNC_ENTITY_TYPES } from 'src/utils/constants';
import { pilotageTugsSelector } from 'src/store/pilotageTugs/selectors';
import { pilotageTugsLoaded } from 'src/store/pilotageTugs/actions';
import { createSyncUuid } from 'src/utils/sync/pilotageTugs';

const PILOTAGE_TUG_STAGES_BASE = 'PILOTAGE_TUG_STAGES';
const PILOTAGE_TUG_STAGES_ADD_BASE = 'PILOTAGE_TUG_STAGES_ADD';
const PILOTAGE_TUG_STAGES_UPDATE_BASE = 'PILOTAGE_TUG_STAGES_UPDATE';
const PILOTAGE_TUG_STAGES_DELETE_BASE = 'PILOTAGE_TUG_STAGES_DELETE';
const PILOTAGE_TUG_STAGES_REORDER_BASE = 'PILOTAGE_TUG_STAGES_REORDER';

export const [
  PILOTAGE_TUG_STAGES_REQUEST,
  PILOTAGE_TUG_STAGES_SUCCESS,
  PILOTAGE_TUG_STAGES_ERROR,
  PILOTAGE_TUG_STAGES_CANCEL,
  PILOTAGE_TUG_STAGES_IN_PROGRESS,
] = createBasicActionTypes(PILOTAGE_TUG_STAGES_BASE);

export const [
  PILOTAGE_TUG_STAGES_ADD_REQUEST,
  PILOTAGE_TUG_STAGES_ADD_SUCCESS,
  PILOTAGE_TUG_STAGES_ADD_ERROR,
  PILOTAGE_TUG_STAGES_ADD_CANCEL,
  PILOTAGE_TUG_STAGES_ADD_IN_PROGRESS,
] = createBasicActionTypes(PILOTAGE_TUG_STAGES_ADD_BASE);

export const [
  PILOTAGE_TUG_STAGES_UPDATE_REQUEST,
  PILOTAGE_TUG_STAGES_UPDATE_SUCCESS,
  PILOTAGE_TUG_STAGES_UPDATE_ERROR,
  PILOTAGE_TUG_STAGES_UPDATE_CANCEL,
  PILOTAGE_TUG_STAGES_UPDATE_IN_PROGRESS,
] = createBasicActionTypes(PILOTAGE_TUG_STAGES_UPDATE_BASE);

export const [
  PILOTAGE_TUG_STAGES_DELETE_REQUEST,
  PILOTAGE_TUG_STAGES_DELETE_SUCCESS,
  PILOTAGE_TUG_STAGES_DELETE_ERROR,
  PILOTAGE_TUG_STAGES_DELETE_CANCEL,
  PILOTAGE_TUG_STAGES_DELETE_IN_PROGRESS,
] = createBasicActionTypes(PILOTAGE_TUG_STAGES_DELETE_BASE);

export const [
  PILOTAGE_TUG_STAGES_REORDER_REQUEST,
  PILOTAGE_TUG_STAGES_REORDER_SUCCESS,
  PILOTAGE_TUG_STAGES_REORDER_ERROR,
  PILOTAGE_TUG_STAGES_REORDER_CANCEL,
  PILOTAGE_TUG_STAGES_REORDER_IN_PROGRESS,
] = createBasicActionTypes(PILOTAGE_TUG_STAGES_REORDER_BASE);

const flattenStages = stages => {
  if (!stages) {
    return [];
  }
  if (Array.isArray(stages)) {
    return stages;
  }
  return Object.values(stages);
};

const flattenPilotageTugs = tugsByStage =>
  Object.values(tugsByStage || {}).flatMap(stageTugs =>
    Object.values(stageTugs || {})
  );

export const pilotageTugStagesRequest = pilotageUuid => async (dispatch, getState) => {
  dispatch(pilotageTugStagesInProgress());
  const state = getState();
  const isOnline = isOnlineSelector(state);
  try {
    let data = [];
    const syncEntityUuid = `pilotage-tug-stages-${pilotageUuid}`;
    const inSyncStages = entityByUuidSelector(syncEntityUuid)(state);
    const { pilotageUuid: storePilotageUuid, data: storeData } = state.pilotageTugStages || {};
    const storeStagesForPilotage = storePilotageUuid === pilotageUuid ? storeData : null;
    const cachedEntityData = inSyncStages && inSyncStages.entity && inSyncStages.entity.data;
    const cachedData = cachedEntityData || storeStagesForPilotage || null;
    const shouldUseCache = !isOnline && cachedData;

    if (!shouldUseCache) {
      const response = await getTugStages(pilotageUuid);
      data = getPayloadFromResponse(response);
    } else {
      data = flattenStages(cachedData);
    }

    dispatch(pilotageTugStagesLoaded(pilotageUuid, data));
    return data;
  } catch (error) {
    dispatch(pilotageTugStagesError(error));
    return false;
  }
};

export const pilotageTugStagesInProgress = () => createInProgressAction(PILOTAGE_TUG_STAGES_BASE);
export const pilotageTugStagesError = error => dispatch => {
  dispatch(createErrorAction(PILOTAGE_TUG_STAGES_BASE, error));
  dispatch(addErrorToast({ message: TOAST_MESSAGES.LOAD_PILOTAGE_TUGS_ERROR }));
};
export const pilotageTugStagesLoaded = (pilotageUuid, data) => createSuccessAction(PILOTAGE_TUG_STAGES_BASE, { pilotageUuid, data });

export const pilotageTugStagesAddRequest = (pilotageUuid, name, sequence) => async (dispatch, getState) => {
  const state = getState();
  const isOnline = isOnlineSelector(state);
  dispatch(pilotageTugStagesAddInProgress());
  try {
    let data;
    if (isOnline) {
      const response = await addTugStage({ name, sequence, pilotage: { uuid: pilotageUuid } });
      data = getPayloadFromResponse(response);
    } else {
      const syncEntityUuid = `pilotage-tug-stages-${pilotageUuid}`;
      const inSyncStages = entityByUuidSelector(syncEntityUuid)(state);
      let currentData = inSyncStages ? inSyncStages.entity.data : {};
      const uuid = uuidv4();
      data = { ...currentData, [uuid]: { uuid, name, sequence, pilotage: { uuid: pilotageUuid } } };
      await dispatch(addEntity({
        uuid: syncEntityUuid,
        type: SYNC_ENTITY_TYPES.PILOTAGE_TUG_STAGES,
        pilotageUuid,
        entity: { uuid: syncEntityUuid, pilotageUuid, data },
      }));
    }
    dispatch(pilotageTugStagesAddLoaded(data));
    return true;
  } catch (error) {
    dispatch(pilotageTugStagesAddError(error));
    return false;
  }
};
export const pilotageTugStagesAddInProgress = () => createInProgressAction(PILOTAGE_TUG_STAGES_ADD_BASE);
export const pilotageTugStagesAddError = error => dispatch => {
  dispatch(createErrorAction(PILOTAGE_TUG_STAGES_ADD_BASE, error));
  dispatch(addErrorToast({ message: TOAST_MESSAGES.ADD_PILOTAGE_TUG_ERROR }));
};
export const pilotageTugStagesAddLoaded = data => dispatch => {
  dispatch(addSuccessToast({ message: 'Stage added' }));
  dispatch(createSuccessAction(PILOTAGE_TUG_STAGES_ADD_BASE, data));
};

export const pilotageTugStagesUpdateRequest = (pilotageUuid, uuid, updates) => async (dispatch, getState) => {
  const state = getState();
  const isOnline = isOnlineSelector(state);
  dispatch(pilotageTugStagesUpdateInProgress());
  try {
    let data;
    if (isOnline) {
      const response = await updateTugStage(uuid, updates);
      data = getPayloadFromResponse(response);
    } else {
      const syncEntityUuid = `pilotage-tug-stages-${pilotageUuid}`;
      const inSyncStages = entityByUuidSelector(syncEntityUuid)(state);
      let currentData = inSyncStages ? inSyncStages.entity.data : {};
      data = { ...currentData, [uuid]: { ...currentData[uuid], ...updates } };
      await dispatch(addEntity({
        uuid: syncEntityUuid,
        type: SYNC_ENTITY_TYPES.PILOTAGE_TUG_STAGES,
        pilotageUuid,
        entity: { uuid: syncEntityUuid, pilotageUuid, data },
      }));
    }
    dispatch(pilotageTugStagesUpdateLoaded(data));
    return true;
  } catch (error) {
    dispatch(pilotageTugStagesUpdateError(error));
    return false;
  }
};
export const pilotageTugStagesUpdateInProgress = () => createInProgressAction(PILOTAGE_TUG_STAGES_UPDATE_BASE);
export const pilotageTugStagesUpdateError = error => dispatch => {
  dispatch(createErrorAction(PILOTAGE_TUG_STAGES_UPDATE_BASE, error));
  dispatch(addErrorToast({ message: 'Failed to update stage' }));
};
export const pilotageTugStagesUpdateLoaded = data => dispatch => {
  dispatch(addSuccessToast({ message: 'Stage updated' }));
  dispatch(createSuccessAction(PILOTAGE_TUG_STAGES_UPDATE_BASE, data));
};

export const pilotageTugStagesDeleteRequest = (pilotageUuid, uuid) => async (dispatch, getState) => {
  dispatch(pilotageTugStagesDeleteInProgress());
  const state = getState();
  const isOnline = isOnlineSelector(state);
  const syncEntityUuid = `pilotage-tug-stages-${pilotageUuid}`;
  const inSyncStages = entityByUuidSelector(syncEntityUuid)(state);
  const storeStages =
    state.pilotageTugStages &&
    state.pilotageTugStages.pilotageUuid === pilotageUuid &&
    state.pilotageTugStages.data
      ? state.pilotageTugStages.data
      : null;
  const baseStages =
    (inSyncStages && inSyncStages.entity && inSyncStages.entity.data) ||
    storeStages ||
    {};
  const normalizedStages = Array.isArray(baseStages)
    ? baseStages.reduce((acc, stage) => {
        if (stage && stage.uuid) {
          acc[stage.uuid] = stage;
        }
        return acc;
      }, {})
    : typeof baseStages === 'object' && baseStages !== null
      ? { ...baseStages }
      : {};

  try {
    if (isOnline) {
      await deleteTugStage(uuid);
    }

    if (normalizedStages[uuid]) {
      delete normalizedStages[uuid];
    }

    if (!isOnline) {
      await dispatch(addEntity({
        uuid: syncEntityUuid,
        type: SYNC_ENTITY_TYPES.PILOTAGE_TUG_STAGES,
        pilotageUuid,
        entity: { uuid: syncEntityUuid, pilotageUuid, data: normalizedStages },
      }));

      const pilotageTugsSyncUuid = createSyncUuid(pilotageUuid);
      const syncPilotageTugs = entityByUuidSelector(pilotageTugsSyncUuid)(getState());
      const syncPilotageTugsData =
        syncPilotageTugs && syncPilotageTugs.entity && syncPilotageTugs.entity.data
          ? syncPilotageTugs.entity.data
          : {};
      const normalizedTugsByStage = Array.isArray(syncPilotageTugsData)
        ? syncPilotageTugsData.reduce((acc, tug) => {
            const stageId = tug.stage && tug.stage.uuid;
            const lineNumber = tug.lineNumber;
            if (!stageId || lineNumber == null) {
              return acc;
            }
            if (!acc[stageId]) {
              acc[stageId] = {};
            }
            acc[stageId][lineNumber] = tug;
            return acc;
          }, {})
        : { ...syncPilotageTugsData };

      if (normalizedTugsByStage[uuid]) {
        delete normalizedTugsByStage[uuid];
        await dispatch(addEntity({
          uuid: pilotageTugsSyncUuid,
          type: SYNC_ENTITY_TYPES.PILOTAGE_TUGS,
          pilotageUuid,
          entity: {
            uuid: pilotageTugsSyncUuid,
            pilotageUuid,
            data: normalizedTugsByStage,
          },
        }));
      }
    }

    dispatch(pilotageTugStagesDeleteLoaded(normalizedStages));

    const pilotageTugsState = pilotageTugsSelector(getState());
    if (
      pilotageTugsState &&
      pilotageTugsState.pilotageUuid === pilotageUuid &&
      pilotageTugsState.data
    ) {
      const currentTugsByStage = Array.isArray(pilotageTugsState.data)
        ? pilotageTugsState.data.reduce((acc, tug) => {
            const stageId = tug.stage && tug.stage.uuid;
            const lineNumber = tug.lineNumber;
            if (!stageId || lineNumber == null) {
              return acc;
            }
            if (!acc[stageId]) {
              acc[stageId] = {};
            }
            acc[stageId][lineNumber] = tug;
            return acc;
          }, {})
        : { ...pilotageTugsState.data };
      if (currentTugsByStage[uuid]) {
        delete currentTugsByStage[uuid];
        const flattenedTugs = flattenPilotageTugs(currentTugsByStage);
        dispatch(pilotageTugsLoaded(pilotageUuid, flattenedTugs));
      }
    }

    return true;
  } catch (error) {
    dispatch(pilotageTugStagesDeleteError(error));
    return false;
  }
};
export const pilotageTugStagesDeleteInProgress = () => createInProgressAction(PILOTAGE_TUG_STAGES_DELETE_BASE);
export const pilotageTugStagesDeleteError = error => dispatch => {
  dispatch(createErrorAction(PILOTAGE_TUG_STAGES_DELETE_BASE, error));
  dispatch(addErrorToast({ message: 'Failed to delete stage' }));
};
export const pilotageTugStagesDeleteLoaded = data => dispatch => {
  dispatch(createSuccessAction(PILOTAGE_TUG_STAGES_DELETE_BASE, data));
};

export const pilotageTugStagesReorderRequest = (pilotageUuid, stages) => async (dispatch, getState) => {
  const state = getState();
  const isOnline = isOnlineSelector(state);
  dispatch(pilotageTugStagesReorderInProgress());
  try {
    let data;
    if (isOnline) {
      const response = await updateTugStages(stages);
      data = getPayloadFromResponse(response);
    } else {
      const syncEntityUuid = `pilotage-tug-stages-${pilotageUuid}`;
      const inSyncStages = entityByUuidSelector(syncEntityUuid)(state);
      let currentData = inSyncStages ? inSyncStages.entity.data : {};
      // stages: [{ uuid, sequence }]
      data = { ...currentData };
      stages.forEach(({ uuid, sequence }) => {
        if (data[uuid]) data[uuid].sequence = sequence;
      });
      await dispatch(addEntity({
        uuid: syncEntityUuid,
        type: SYNC_ENTITY_TYPES.PILOTAGE_TUG_STAGES,
        pilotageUuid,
        entity: { uuid: syncEntityUuid, pilotageUuid, data },
      }));
    }
    dispatch(pilotageTugStagesReorderLoaded(data));
    return true;
  } catch (error) {
    dispatch(pilotageTugStagesReorderError(error));
    return false;
  }
};
export const pilotageTugStagesReorderInProgress = () => createInProgressAction(PILOTAGE_TUG_STAGES_REORDER_BASE);
export const pilotageTugStagesReorderError = error => dispatch => {
  dispatch(createErrorAction(PILOTAGE_TUG_STAGES_REORDER_BASE, error));
  dispatch(addErrorToast({ message: 'Failed to reorder stages' }));
};
export const pilotageTugStagesReorderLoaded = data => dispatch => {
  dispatch(addSuccessToast({ message: 'Stages reordered' }));
  dispatch(createSuccessAction(PILOTAGE_TUG_STAGES_REORDER_BASE, data));
};
